import type { ComponentInternalInstance, Directive, DirectiveBinding, VNode } from 'vue';
import type { IForm } from '@common/composables/useForm';

interface FormElement extends HTMLElement {
	__formSubmitHandler?: (event: Event) => void;
}

/**
 * Relie un `<v-form>` à un formulaire `useForm`.
 *
 * Récupère l'instance Vuetify (pour `validate()` / `resetValidation()`) et
 * intercepte la soumission native, ce qui fait fonctionner la touche Entrée et
 * les boutons `type="submit"` sans câblage supplémentaire.
 *
 * Usage : `<v-form v-form="form">`.
 */
export const vForm: Directive<FormElement, IForm> = {
	mounted(el, binding: DirectiveBinding<IForm>, vnode: VNode) {
		const form = binding.value;
		if (!form) {
			return;
		}

		const instance = (vnode.component ??
			(vnode as VNode & { ctx?: ComponentInternalInstance | null }).ctx) as
			| ComponentInternalInstance
			| null;

		if (instance) {
			form.component = (instance.exposed ?? instance.proxy) as IForm['component'];
		}

		const handler = (event: Event): void => {
			event.preventDefault();
			void form.handle();
		};
		// En capture : on intercepte avant tout gestionnaire posé plus bas.
		el.addEventListener('submit', handler, { capture: true });
		el.__formSubmitHandler = handler;
	},

	beforeUnmount(el) {
		if (el.__formSubmitHandler) {
			el.removeEventListener('submit', el.__formSubmitHandler, { capture: true });
			el.__formSubmitHandler = undefined;
		}
	},
};

export default {
	install(app: { directive: (name: string, directive: Directive) => void }) {
		app.directive('form', vForm as Directive);
	},
};
